import React, {useState, memo} from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import MUIContainer from '@/components/MUI-Components/MUI-Container';
import Grid from '@mui/material/Grid';
import {fadeInUp} from '@/animation/scroll-animation';
import dynamic from 'next/dynamic';
import parse from 'html-react-parser';
import {borderRadius, colors, fontWeight, shadows, zIndex} from '@/styles/tokens';

const AnimatedComponent = dynamic(() => import('../AnimatedComponent').then((mod) => mod.AnimatedComponent));

const tabListSx = {
    border: '1px solid #E0E0E0',
    overflow: 'visible',
    height: '100%',
    display: 'flex',
    flexDirection: 'column'
} as const;

const tabItemSx = (active: boolean) => ({
    p: 2,
    flexGrow: 1,
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    bgcolor: active ? '#fff' : '#0B2E5C',
    color: active ? '#000' : '#fff',
    position: 'relative',
    fontFamily: "'Headers', sans-serif",
    zIndex: active ? 10 : 1,
    '&:last-child': {
        borderBottom: 'none'
    }
});

const stepTitleSx = {
    fontWeight: fontWeight.bold,
    fontSize: '16px',
    lineHeight: 1.4,
    fontFamily: "'Headers', sans-serif"
} as const;

const contentBoxSx = {
    bgcolor: '#fff',
    border: '1px solid #E0E0E0',
    borderRadius: borderRadius.lg,
    px: {xs: 3, md: 5},
    py: {xs: 4, md: 5},
    height: '100%',
    boxShadow: shadows.lg,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    minHeight: {xs: 'auto', md: 500},
    position: 'relative',
    zIndex: zIndex.raised
} as const;

const contentTextSx = {
    fontSize: '16px',
    color: colors.text.primary,
    lineHeight: 1.8,
    mb: 2,
    fontFamily: "'RegularText', sans-serif"
} as const;

interface ProcessProps {
    heading: string;
    subheading: string;
    processData: {
        step: string;
        content: string[];
    }[];
}

const Process = ({heading, subheading, processData}: ProcessProps) => {
    const [activeTab, setActiveTab] = useState(0);

    return (
        <Box>
            <MUIContainer>
                <Box textAlign="center" maxWidth={1024} margin="0 auto" mb={5}>
                    <Typography variant="h1" component="h2" mb={3} fontSize={{xs: 35, sm: 48}} className="font-headers">
                        {heading}
                    </Typography>

                    <Typography
                        variant="subtitle1"
                        component="p"
                        className="font-regular-text generative-transforming-subheading"
                        sx={{mb: {xs: 2, md: 6}}}
                    >
                        {subheading}
                    </Typography>
                </Box>

                <Grid container spacing={0} sx={{columnGap: {md: '30px'}, rowGap: {xs: 4, md: 0}}} alignItems="stretch">
                    <Grid item xs={12} md={4} sx={{position: 'relative'}}>
                        <AnimatedComponent variants={fadeInUp}>
                            <Box sx={tabListSx}>
                                {processData?.map((item, index) => {
                                    const active = activeTab === index;
                                    return (
                                        <Box key={index} sx={tabItemSx(active)} onClick={() => setActiveTab(index)}>
                                            <Typography component="h3" className="font-headers" sx={stepTitleSx}>
                                                {item.step}
                                            </Typography>
                                            {active && (
                                                <Box
                                                    sx={{
                                                        position: 'absolute',
                                                        top: -1,
                                                        bottom: -1,
                                                        right: -31,
                                                        width: 31,
                                                        zIndex: zIndex.elevated,
                                                        display: {xs: 'none', md: 'block'}
                                                    }}
                                                >
                                                    <svg width="100%" height="100%" viewBox="0 0 31 100" preserveAspectRatio="none">
                                                        <path
                                                            d="M0,0 L31,50 L0,100"
                                                            fill="white"
                                                            stroke="#E0E0E0"
                                                            strokeWidth="1"
                                                            vectorEffect="non-scaling-stroke"
                                                        />
                                                        <rect x="-1" y="0.5" width="3" height="99" fill="white" />
                                                    </svg>
                                                </Box>
                                            )}
                                        </Box>
                                    );
                                })}
                            </Box>
                        </AnimatedComponent>
                    </Grid>
                    <Grid item xs={12} md={true}>
                        <AnimatedComponent variants={fadeInUp} delay={0.2} style={{height: '100%'}}>
                            {/* Render every step's content so all of it is in the server
                                HTML for crawlers (SEO #33); show only the active step.
                                Hidden via sx display (contentBoxSx sets display:flex, which
                                would override the `hidden` attribute). */}
                            {processData?.map((proc, procIdx) => (
                                <Box
                                    key={procIdx}
                                    className="remove-underline"
                                    sx={{...contentBoxSx, display: activeTab === procIdx ? 'flex' : 'none'}}
                                >
                                    {proc?.content?.map((text, i) => (
                                        <Typography key={i} sx={contentTextSx}>
                                            {parse(text)}
                                        </Typography>
                                    ))}
                                </Box>
                            ))}
                        </AnimatedComponent>
                    </Grid>
                </Grid>
            </MUIContainer>
        </Box>
    );
};

export default memo(Process);
